import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { sbFetch } from "../utils/supabaseClient";
import { sendNotification } from "../utils/notifications";
import { useToast } from "./Toast";
import { parseMarkdown } from "../utils/markdownParser";
import PreviewModal from "./PreviewModal";
import NewsEditModal from "./NewsEditModal";
import PullToRefresh from "./PullToRefresh";
import { SVG_ICONS } from "./TablerIconPicker";
import "../styles/ListTab.css";

const NewsListTab = forwardRef(({ onHeaderInfoChange }, ref) => {
  const { success, error } = useToast();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  useEffect(() => {
    loadNews();
  }, []);

  useImperativeHandle(ref, () => ({
    loadNews
  }));

  useEffect(() => {
    onHeaderInfoChange?.({ title: "News", count: news.length });
  }, [news, onHeaderInfoChange]);

  const loadNews = async () => {
    try {
      setLoading(true);
      const data = await sbFetch("GET", "news?order=published_at.desc");
      setNews(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading news:", err);
      error("Failed to load news");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
  };

  const handleSaveEdit = async (updatedNews) => {
    const oldNews = news.find(n => n.id === editingId);
    const wasPublished = oldNews?.draft && !updatedNews.draft;

    setNews(news.map(n => n.id === editingId ? { ...n, ...updatedNews } : n));

    if (wasPublished) {
      await sendNotification(
        `📰 New News Item • ${updatedNews.title}`,
        updatedNews.title,
        "news",
        editingId
      );
    }

    setEditingId(null);
    success("News item updated");
  };

  const handleCancel = () => {
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this news item?")) return;

    try {
      await sbFetch("DELETE", `news?id=eq.${id}`);
      setNews(news.filter((n) => n.id !== id));
      success("News item deleted");
    } catch (err) {
      console.error("Error deleting news:", err);
      error("Failed to delete news item");
    }
  };

  const handlePreview = (item) => {
    const content = (
      <div style={{ width: "100%", maxWidth: "100%" }}>
        <h1 style={{ color: "#5B9EF0", marginTop: 0 }}>{item.title}</h1>
        <div style={{ color: "#7A9CC4", fontSize: "14px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "6px" }}>
          {SVG_ICONS["calendar"]({ size: 16, color: "#7A9CC4" })}
          {item.published_at ? new Date(item.published_at).toLocaleDateString() : "No date"}
        </div>
        {item.image_url && (
          <img
            src={item.image_url}
            alt={item.title}
            style={{
              maxWidth: "100%",
              height: "auto",
              borderRadius: "8px",
              marginBottom: "20px",
            }}
          />
        )}
        {item.description && (
          <div dangerouslySetInnerHTML={{ __html: parseMarkdown(item.description) }} style={{ marginBottom: "20px" }} />
        )}
        <div style={{ color: "#7A9CC4", fontSize: "14px" }}>
          Link:{" "}
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#3B82F6",
              textDecoration: "none",
              wordBreak: "break-all",
            }}
          >
            {item.url}
          </a>
        </div>
      </div>
    );

    setPreviewTitle(`News: ${item.title}`);
    setPreviewContent(content);
    setPreviewOpen(true);
  };

  const handleToggleDraft = async (item) => {
    try {
      const newDraftStatus = !item.draft;

      // When publishing (draft -> published), set published_at if not already set
      const publishedAt = !newDraftStatus && !item.published_at ? new Date().toISOString().split('T')[0] : item.published_at;

      await sbFetch("PATCH", `news?id=eq.${item.id}`, {
        draft: newDraftStatus,
        published_at: publishedAt
      });

      const updatedNews = news.map(n =>
        n.id === item.id ? { ...n, draft: newDraftStatus, published_at: publishedAt } : n
      );
      setNews(updatedNews);

      if (!newDraftStatus && item.draft) {
        // Publishing: send notification
        await sendNotification(
          `📰 New News Item • ${item.title}`,
          item.title,
          "news",
          item.id
        );
      }
      success(newDraftStatus ? "Moved to draft" : "Published");
    } catch (err) {
      console.error("Error toggling draft status:", err);
      error("Failed to update news item status");
    }
  };

  if (loading) return <div className="loading">Loading news...</div>;

  if (news.length === 0)
    return <div className="empty-state">No news items found. Create one first!</div>;

  const filteredNews = news.filter((item) => {
    // Filter by search query
    const matchesSearch =
      item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.url?.toLowerCase().includes(searchQuery.toLowerCase());

    // Filter by selected status (Published vs Draft)
    const matchesStatus = selectedStatus === null || (
      (selectedStatus === "published" && !item.draft) ||
      (selectedStatus === "draft" && item.draft)
    );

    return matchesSearch && matchesStatus;
  });

  const editingNews = news.find((n) => n.id === editingId);

  return (
    <div className="list-container">
      <PreviewModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        content={previewContent}
        title={previewTitle}
      />

      {editingNews && (
        <NewsEditModal
          news={editingNews}
          onSave={handleSaveEdit}
          onCancel={handleCancel}
        />
      )}

      <input
        type="text"
        placeholder="Search news..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{
          width: "100%",
          padding: "10px 12px",
          marginBottom: "12px",
          background: "#050e1a",
          border: "1px solid rgba(59, 130, 246, 0.2)",
          borderRadius: "6px",
          color: "#fff",
          fontSize: "14px",
          boxSizing: "border-box",
        }}
      />

      {/* Status Filter Pills */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}>
        <button
          onClick={() => setSelectedStatus(null)}
          style={{
            padding: "6px 14px",
            background: selectedStatus === null ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
            color: selectedStatus === null ? "#5B9EF0" : "#7A9CC4",
            border: selectedStatus === null ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
            borderRadius: "20px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "500",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            if (selectedStatus !== null) {
              e.target.style.background = "rgba(59, 130, 246, 0.1)";
              e.target.style.borderColor = "rgba(59, 130, 246, 0.3)";
            }
          }}
          onMouseLeave={(e) => {
            if (selectedStatus !== null) {
              e.target.style.background = "rgba(59, 130, 246, 0.05)";
              e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
            }
          }}
        >
          All
        </button>
        {["published", "draft"].map((status) => {
          const count = news.filter(item => (
            status === "published" ? !item.draft : item.draft
          )).length;
          const label = status.charAt(0).toUpperCase() + status.slice(1);
          return (
            <button
              key={status}
              onClick={() => setSelectedStatus(status)}
              style={{
                padding: "6px 14px",
                background: selectedStatus === status ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.05)",
                color: selectedStatus === status ? "#5B9EF0" : "#7A9CC4",
                border: selectedStatus === status ? "1px solid rgba(59, 130, 246, 0.4)" : "1px solid rgba(59, 130, 246, 0.2)",
                borderRadius: "20px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "500",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                if (selectedStatus !== status) {
                  e.target.style.background = "rgba(59, 130, 246, 0.1)";
                  e.target.style.borderColor = "rgba(59, 130, 246, 0.3)";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedStatus !== status) {
                  e.target.style.background = "rgba(59, 130, 246, 0.05)";
                  e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                }
              }}
            >
              {label} ({count})
            </button>
          );
        })}
      </div>

      <PullToRefresh onRefresh={loadNews}>
        {filteredNews.length === 0 && searchQuery && (
          <div className="empty-state">No news match your search</div>
        )}
        {filteredNews.length > 0 && (
          <div className="items-grid">
        {filteredNews.map((item) => (
          <div key={item.id} className="item-card">
            {item.image_url && (
              <img src={item.image_url} alt={item.title} style={{ width: "100%", height: "150px", objectFit: "cover", borderRadius: "8px", marginBottom: "12px" }} />
            )}
            <div className="item-content">
              <h4>{item.title}</h4>
              <p className="item-meta" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                {SVG_ICONS["calendar"]({ size: 14, color: "#7A9CC4" })}
                {item.published_at ? new Date(item.published_at).toLocaleDateString() : "No date"}
              </p>
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="item-subtitle"
                style={{ fontSize: "12px", color: "#3B82F6", wordBreak: "break-all", overflowWrap: "break-word", whiteSpace: "normal", overflow: "hidden", margin: "4px 0", textDecoration: "none", cursor: "pointer" }}
              >
                {item.url}
              </a>
              <div style={{display: "flex", gap: "8px", marginTop: "8px"}}>
                {item.draft ? (
                  <div style={{display: "flex", alignItems: "center", gap: "4px", padding: "4px 12px", background: "rgba(245, 158, 11, 0.2)", color: "#F59E0B", borderRadius: "6px", fontSize: "12px", fontWeight: "600"}}>
                    {SVG_ICONS["alert-circle"]({ size: 14, color: "#F59E0B" })}
                    DRAFT
                  </div>
                ) : (
                  <div style={{display: "flex", alignItems: "center", gap: "4px", padding: "4px 12px", background: "#22C55E20", color: "#22C55E", borderRadius: "6px", fontSize: "12px", fontWeight: "600"}}>
                    {SVG_ICONS["check-circle"]({ size: 14, color: "#22C55E" })}
                    PUBLISHED
                  </div>
                )}
              </div>
            </div>
            <div className="item-actions">
              <button
                onClick={() => handlePreview(item)}
                title="Preview"
                style={{
                  padding: "6px 12px",
                  background: "rgba(59, 130, 246, 0.1)",
                  color: "#5B9EF0",
                  border: "1px solid rgba(59, 130, 246, 0.2)",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "rgba(59, 130, 246, 0.2)";
                  e.target.style.borderColor = "rgba(59, 130, 246, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "rgba(59, 130, 246, 0.1)";
                  e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                }}
              >
                {SVG_ICONS["eye"]({ size: 18, color: "#5B9EF0" })}
              </button>
              <button
                onClick={() => handleToggleDraft(item)}
                title={item.draft ? "Publish news" : "Revert to draft"}
                style={{
                  padding: "6px 12px",
                  background: item.draft ? "rgba(34, 197, 94, 0.1)" : "rgba(245, 158, 11, 0.1)",
                  color: item.draft ? "#22C55E" : "#F59E0B",
                  border: item.draft ? "1px solid rgba(34, 197, 94, 0.2)" : "1px solid rgba(245, 158, 11, 0.2)",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                }}
                onMouseEnter={(e) => {
                  if (item.draft) {
                    e.target.style.background = "rgba(34, 197, 94, 0.2)";
                    e.target.style.borderColor = "rgba(34, 197, 94, 0.4)";
                  } else {
                    e.target.style.background = "rgba(245, 158, 11, 0.2)";
                    e.target.style.borderColor = "rgba(245, 158, 11, 0.4)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (item.draft) {
                    e.target.style.background = "rgba(34, 197, 94, 0.1)";
                    e.target.style.borderColor = "rgba(34, 197, 94, 0.2)";
                  } else {
                    e.target.style.background = "rgba(245, 158, 11, 0.1)";
                    e.target.style.borderColor = "rgba(245, 158, 11, 0.2)";
                  }
                }}
              >
                {item.draft
                  ? SVG_ICONS["check"]({ size: 18, color: "#22C55E" })
                  : SVG_ICONS["circle-x"]({ size: 18, color: "#F59E0B" })}
                <span style={{ fontSize: "12px" }}>
                  {item.draft ? "Publish" : "Draft"}
                </span>
              </button>
              <button
                onClick={() => handleEdit(item)}
                title="Edit"
                style={{
                  padding: "6px 12px",
                  background: "rgba(59, 130, 246, 0.1)",
                  color: "#5B9EF0",
                  border: "1px solid rgba(59, 130, 246, 0.2)",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "rgba(59, 130, 246, 0.2)";
                  e.target.style.borderColor = "rgba(59, 130, 246, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "rgba(59, 130, 246, 0.1)";
                  e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                }}
              >
                {SVG_ICONS["pencil"]({ size: 18, color: "#5B9EF0" })}
              </button>
              <button
                onClick={() => handleDelete(item.id)}
                style={{
                  padding: "6px 12px",
                  background: "rgba(59, 130, 246, 0.1)",
                  color: "#5B9EF0",
                  border: "1px solid rgba(59, 130, 246, 0.2)",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "rgba(59, 130, 246, 0.2)";
                  e.target.style.borderColor = "rgba(59, 130, 246, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "rgba(59, 130, 246, 0.1)";
                  e.target.style.borderColor = "rgba(59, 130, 246, 0.2)";
                }}
              >
                {SVG_ICONS["trash"]({ size: 18, color: "#5B9EF0" })}
              </button>
            </div>
          </div>
        ))}
        </div>
        )}
      </PullToRefresh>
    </div>
  );
});

NewsListTab.displayName = 'NewsListTab';

export default NewsListTab;
